import { useCallback, useRef, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { useProjectStore } from '../stores/projectStore';
import { AppNode, NodeType, NodeStatus, DetailPageStage } from '../types';
import { projectApi } from '../services/projectApi';
import { isBackendAssetUrl, normalizeAssetUrl } from '../utils/assetUtils';

export function useNodeOperations(saveHistory: () => void, setAssetHistory: Dispatch<SetStateAction<any[]>>) {
    const nodes = useNodeStore((s) => s.nodes);
    const addNode = useNodeStore((s) => s.addNode);
    const updateNode = useNodeStore((s) => s.updateNode);
    const deleteNodes = useNodeStore((s) => s.deleteNodes);
    const nodesRef = useRef(nodes);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);

    // 第 4 参 prompt：生成该资产的真实提示词（上传作品选历史图库时回填提示词栏）；缺省回落 title
    const handleAssetGenerated = useCallback((type: 'image' | 'video' | 'audio', src: string, title: string, prompt?: string) => {
        setAssetHistory(h => {
            const exists = h.find(a => a.src === src);
            if (exists) return h;
            return [{ id: `a-${Date.now()}`, type, src, title, timestamp: Date.now(), prompt: (prompt || title || '').trim() || undefined }, ...h];
        });
    }, [setAssetHistory]);

    const handleAddNode = useCallback((type: NodeType, position?: { x: number; y: number }, initialData?: any) => {
        if (type === NodeType.IMAGE_EDITOR) {
            return 'open_sketch_editor';
        }

        try { saveHistory(); } catch (e) { }

        const isDetailPage = type === NodeType.DETAIL_PAGE;

        const defaults: any = isDetailPage
            ? {
                stage: DetailPageStage.CONFIG,
                model: 'grsai-gemini-3.1-flash-lite',
                planner: undefined,
                designer: undefined,
                ...initialData
            }
            : {
                model: type === NodeType.VIDEO_GENERATOR ? 'grsai-minimax-h3' :
                       // 脚本节点走 LLM 对话通道，不落图像模型兜底（生成时 modelId 留空走后端默认）
                       // 灵感音乐（AUDIO_GENERATOR）走 /api/audio/tts 同步通道，不依赖模型
                       (type === NodeType.MEDIA_UPLOAD || type === NodeType.SCRIPT_NODE || type === NodeType.AUDIO_GENERATOR || type === NodeType.TEXT_NODE) ? undefined :
                       type.includes('IMAGE') ? 'grsai-gpt-image-2' :
                       'grsai-nano-banana-pro',
                aspectRatio: (type === NodeType.IMAGE_GENERATOR || type === NodeType.VIDEO_GENERATOR) ? '16:9' : undefined,
                generationMode: type === NodeType.VIDEO_GENERATOR ? 'DEFAULT' : undefined,
                ...initialData
            };

        const typeMap: Record<string, string> = {
            [NodeType.PROMPT_INPUT]: '创意描述',
            [NodeType.MEDIA_UPLOAD]: '媒体上传',
            [NodeType.IMAGE_GENERATOR]: '图片生成',
            [NodeType.VIDEO_GENERATOR]: '视频生成',
            [NodeType.AUDIO_GENERATOR]: '灵感音乐',
            [NodeType.IMAGE_EDITOR]: '图像编辑',
            [NodeType.DIRECTOR]: '导演台',
            [NodeType.LAYER_EDIT]: '图层编辑',
            [NodeType.DETAIL_PAGE]: '详情页',
            [NodeType.SCRIPT_NODE]: '脚本',
            [NodeType.VIDEO_COMPOSE]: '智能剪辑',
            [NodeType.TEXT_NODE]: '文本节点'
        };

        const safeX = position?.x ?? 100;
        const safeY = position?.y ?? 100;

        const newNode: AppNode = {
            id: `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            type,
            x: isNaN(safeX) ? 100 : safeX,
            y: isNaN(safeY) ? 100 : safeY,
            width: 420,
            title: typeMap[type] || '未命名节点',
            status: NodeStatus.IDLE,
            data: defaults,
            inputs: []
        };

        addNode(newNode);
        return newNode.id;
    }, [addNode, saveHistory]);

    const handleNodeUpdate = useCallback((id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => {
        updateNode(id, data, size, title);
        // 资产登记携带节点真实提示词（node.data.prompt），上传作品选历史图库时回填提示词栏
        const nodePrompt = typeof nodesRef.current.find(n => n.id === id)?.data?.prompt === 'string'
            ? (nodesRef.current.find(n => n.id === id)!.data as { prompt: string }).prompt : undefined;
        if (data.image) handleAssetGenerated('image', data.image, title || 'Image', nodePrompt);
        if (data.videoUri) handleAssetGenerated('video', data.videoUri, title || 'Video', nodePrompt);
        if (data.audioUri) handleAssetGenerated('audio', data.audioUri, title || 'Audio', nodePrompt);
    }, [updateNode, handleAssetGenerated]);

    // 文档 5.5：UI 编辑专用，操作前保存历史快照；生成链路继续用 handleNodeUpdate（不记历史）
    const handleNodeUpdateWithHistory = useCallback((id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => {
        saveHistory();
        handleNodeUpdate(id, data, size, title);
    }, [saveHistory, handleNodeUpdate]);

    const handleNodeDelete = useCallback((id: string) => {
        saveHistory();
        deleteNodes([id]);
    }, [deleteNodes, saveHistory]);

    const handleExtractImage = useCallback(async (nodeId: string, image: string) => {
        const sourceNode = nodesRef.current.find(n => n.id === nodeId);
        if (!sourceNode) return;

        // 先创建节点（data URL 占位），保证即时反馈
        // 计算源节点的实际渲染宽度：短边固定 420，长边按 aspectRatio 计算
        // 视频节点 16:9 时实际宽度约 747，直接用 node.width(420) 会重叠
        const SHORT_EDGE = 420;
        const ratio = sourceNode.data?.aspectRatio || '16:9';
        const [rw, rh] = ratio.split(':').map(Number);
        const isLandscape = rw >= rh;
        const computedWidth = isLandscape ? SHORT_EDGE * rw / rh : SHORT_EDGE;
        const sourceWidth = Math.max(sourceNode.width || SHORT_EDGE, computedWidth);
        const newX = sourceNode.x + sourceWidth + 100;
        const newY = sourceNode.y;
        const newId = handleAddNode(NodeType.MEDIA_UPLOAD, { x: newX, y: newY }, { image });

        // 连线：源节点 → 截帧节点（从源节点右侧出，到截帧节点左侧入）
        if (newId) {
            useNodeStore.getState().addConnection(nodeId, newId);
            // 在新节点的 inputs 中记录源节点
            useNodeStore.setState((state) => ({
                nodes: state.nodes.map(n => n.id === newId ? { ...n, inputs: [nodeId] } : n),
            }));
        }

        // 异步上传截帧图片到后端对应画布文件夹
        const projectId = useProjectStore.getState().currentProjectId;
        if (projectId && !isBackendAssetUrl(image) && image.startsWith('data:')) {
            try {
                // data URL → Blob → File
                const response = await fetch(image);
                const blob = await response.blob();
                const ext = blob.type.split('/')[1] || 'png';
                const file = new File([blob], `frame_${Date.now()}.${ext}`, { type: blob.type });
                const asset = await projectApi.uploadAsset(projectId, file, {
                    generator_node_id: nodeId,
                    generator_type: 'video_frame',
                });
                const backendUrl = normalizeAssetUrl(asset.url);
                // 上传成功后用后端 URL 替换 data URL
                useNodeStore.getState().updateNode(newId!, { image: backendUrl });
            } catch (err: any) {
                console.error('[截帧] 上传到后端失败:', err.message);
                // 失败时保留 data URL，不阻断用户操作
            }
        }
    }, [handleAddNode]);

    return {
        handleAddNode,
        handleNodeUpdate,
        handleNodeUpdateWithHistory,
        handleNodeDelete,
        handleExtractImage,
        handleAssetGenerated,
    };
}
